import styled from 'styled-components';

import { BoldRenderer } from '../plugins/bold/components/bold';
import { ItalicRenderer } from '../plugins/italic/components/italic';
import { UnderlineRenderer } from '../plugins/underline/components/underline';
import { ParagraphRenderer } from '../plugins/paragraph/components/paragraph';
import {
  InlineBookmarkRenderer,
  ReadonlyInlineBookmarkRenderer,
} from '../plugins/inlineBookmark/components/InlineBookmark';
import { OrderedListRenderer, UnorderedListRenderer } from '../plugins/list/components/List';
import { PicklistRenderer } from '../plugins/picklist/components/PicklistElement';
import {
  PlaceholderReadOnlyRenderer,
  PlaceholderRenderElement as PlaceholderRenderer,
} from '../plugins/placeholder/components/Placeholder';
import { HighlightedRenderer } from '../plugins/highlighted/components/highlighted';
import { HeadingRenderer } from '../plugins/heading/components/heading';
import { LineBreakRenderer } from '../plugins/lineBreak/components/lineBreak';
import { TextSourceStylingRenderer } from '../plugins/textSourceStyling/components/TextSourceStyling';
import { HeadingErrorTextRenderer } from '../plugins/headingError/components/headingError';
import type { ElementPlugin } from '../plugins/types';
import { ElementComponent } from './Element';
import { unreachableCaseLog } from 'types';
import type { RendererRenderElement, RendererRenderText } from './types';
import { SectionHeaderRenderer } from '../plugins/sectionHeader/components/index';
import { DeepLinkRenderer, ReadonlyDeepLinkRenderer } from '../plugins/deepLink/components/index';
import type { Styles } from 'generated/graphql';
import { getDefaultReporterStyles } from 'hooks/useReporterStyles';
import { LIST_VARIANTS } from '../plugins/list/constants';
import { DICTATION_ID_TEXT_MARK, DICTATION_INDEX_TEXT_MARK } from '../plugins';
import type { CSSProperties } from 'react';

export type RendererProps = Readonly<{
  content: ElementPlugin[];
  renderElement?: RendererRenderElement;
  renderText?: RendererRenderText;
  style?: Styles;
  className?: string;
  isReadOnly?: boolean;
}>;

const RendererContainer = styled.div``;

const defaultRenderText: RendererRenderText = ({ mark, children, leaf, text, attributes }) => {
  switch (mark) {
    case 'bold':
      return <BoldRenderer>{children}</BoldRenderer>;
    case 'italic':
      return <ItalicRenderer>{children}</ItalicRenderer>;
    case 'underline':
      return <UnderlineRenderer>{children}</UnderlineRenderer>;
    case 'highlighted':
      return <HighlightedRenderer>{children}</HighlightedRenderer>;
    case 'headingError':
      return <HeadingErrorTextRenderer>{children}</HeadingErrorTextRenderer>;
    case 'source':
      return (
        <TextSourceStylingRenderer attributes={attributes} leaf={leaf} text={text}>
          {children}
        </TextSourceStylingRenderer>
      );
    case DICTATION_ID_TEXT_MARK:
    case DICTATION_INDEX_TEXT_MARK:
      // dictationId and dictationIndex marks are internal only and should not be rendered
      return children;
    default:
      unreachableCaseLog(mark);
      return children;
  }
};

const defaultRenderElement: RendererRenderElement = ({ children, node }) => {
  switch (node.type) {
    case 'inlineBookmark':
      return <InlineBookmarkRenderer element={node}>{children}</InlineBookmarkRenderer>;
    case 'paragraph':
      return <ParagraphRenderer element={node}>{children}</ParagraphRenderer>;
    case 'list':
      if (node.variant === LIST_VARIANTS.ol) {
        return <OrderedListRenderer>{children}</OrderedListRenderer>;
      } else if (node.variant === LIST_VARIANTS.ul) {
        return <UnorderedListRenderer>{children}</UnorderedListRenderer>;
      } else return null;
    case 'picklist':
      return <PicklistRenderer element={node}>{children}</PicklistRenderer>;
    case 'placeholder':
      return <PlaceholderRenderer element={node}>{children}</PlaceholderRenderer>;
    case 'sectionHeader':
      return <SectionHeaderRenderer element={node}>{children}</SectionHeaderRenderer>;
    case 'deepLink':
      return <DeepLinkRenderer element={node}>{children}</DeepLinkRenderer>;
    case 'heading':
      return <HeadingRenderer element={node}>{children}</HeadingRenderer>;
    case 'lineBreak':
      return <LineBreakRenderer>{children}</LineBreakRenderer>;
    default:
      unreachableCaseLog(node.type);
      return null;
  }
};

export const readOnlyRenderElement: RendererRenderElement = ({ children, node }) => {
  switch (node.type) {
    case 'inlineBookmark':
      return (
        <ReadonlyInlineBookmarkRenderer element={node} leftDelimiter="" rightDelimiter="">
          {children}
        </ReadonlyInlineBookmarkRenderer>
      );
    case 'paragraph':
      return <ParagraphRenderer element={node}>{children}</ParagraphRenderer>;
    case 'list':
      if (node.variant === LIST_VARIANTS.ol) {
        return <OrderedListRenderer>{children}</OrderedListRenderer>;
      } else if (node.variant === LIST_VARIANTS.ul) {
        return <UnorderedListRenderer>{children}</UnorderedListRenderer>;
      } else return null;
    case 'picklist':
      return (
        <PicklistRenderer leftDelimiter="" rightDelimiter="" element={node}>
          {children}
        </PicklistRenderer>
      );
    case 'placeholder':
      return <PlaceholderReadOnlyRenderer element={node}>{children}</PlaceholderReadOnlyRenderer>;
    case 'sectionHeader':
      return <SectionHeaderRenderer element={node}>{children}</SectionHeaderRenderer>;
    case 'deepLink':
      return <ReadonlyDeepLinkRenderer element={node}>{children}</ReadonlyDeepLinkRenderer>;
    case 'heading':
      return <HeadingRenderer element={node}>{children}</HeadingRenderer>;
    case 'lineBreak':
      return <LineBreakRenderer>{children}</LineBreakRenderer>;
    default:
      unreachableCaseLog(node.type);
      return null;
  }
};

export const Renderer = ({
  content,
  renderElement,
  renderText = defaultRenderText,
  style = getDefaultReporterStyles(),
  isReadOnly = false,
  ...rest
}: RendererProps): React.ReactElement => {
  const cssProperties: CSSProperties = {
    fontFamily: style?.bodyStyle?.fontFamily ?? undefined,
    fontSize: style?.bodyStyle?.fontSize ?? undefined,
    textTransform: style?.bodyStyle?.textTransform ?? undefined,
    fontWeight: style?.bodyStyle?.textDecoration?.isBold === true ? 'bold' : undefined,
    fontStyle: style?.bodyStyle?.textDecoration?.isItalic === true ? 'italic' : undefined,
    textDecoration:
      style?.bodyStyle?.textDecoration?.isUnderline === true ? 'underline' : undefined,
  };
  return (
    <RendererContainer {...rest} style={cssProperties}>
      {content.map((node, index) => (
        <ElementComponent
          key={index}
          node={node}
          renderElement={
            renderElement ?? (isReadOnly ? readOnlyRenderElement : defaultRenderElement)
          }
          renderText={renderText}
        />
      ))}
    </RendererContainer>
  );
};
